<template>
    <div class="cockpit">
        <!-- this part here is only the beginning overlay of Aircraft Pic and Fake Counters -->
        <transition name="overlay">
            <div v-show="loading">
                <v-img
                    cover
                    height="100vh"
                    width="100vw"
                    class="loadingOverlay"
                    :class="aircraftConfig.selected.toLowerCase()"
                    :src="loadingPic"
                />
                <LoadingCards :counting="counting" class="loadingBar"/>
            </div>
        </transition>

        <!-- Here comes the Overlay screen -->
        <transition name="fade">
            <div v-show="screenOn" class="darker" @click="screenSwitch({value: false, procedure: null})"></div>
        </transition>
        <transition name="zoomer">
            <screen v-show="screenOn" @screenSwitch="screenSwitch" @startSlider="startSlider" :procedures="procedures"/>
        </transition>

        <!-- Here starts the real Cockpit -->
        <transition name="zoomer">
            <slider
                v-show="sliderOn && !screenOn"
                v-if="teacher"
                class="slider-center"
                @toggleHandbook="toggleHandbook"
                @sliderSwitch="sliderSwitch"
                @screen-switch="screenSwitch"
                @next-procedure="switchToNextProcedure"
                :teacher="teacher"
                :hide-step-text-in-blind-exam="false"
                :handbookview="handbookview"
                v-bind:key="activeProcedure"
            />
        </transition>

        <transition name="slider-left-trans">
            <CockpitSlider
                key="1"
                v-if="sliderVisible(sliderLeft)"
                class="cockpit-slider"
                side="left"
                :action-type="sliderLeft"
            />
        </transition>
        <transition name="slider-right-trans">
            <CockpitSlider
                key="2"
                v-if="sliderVisible(sliderRight)"
                :action-type="sliderRight"
                class="cockpit-slider"
                side="right"
                :teacher="teacher"
            />
        </transition>
        <Panorama @loadingDone="loadingDone" @screenSwitch="screenSwitch"/>
        <HeaderOverlay style="position: fixed; top:0; left: 0" @screenSwitch="screenSwitch"/>
        <Infospots v-if="teacher && teacher.getSteps()" :teacher="teacher"/>
    </div>
</template>

<script>
import * as THREE from "three";

import LoadingCards from "@/components/LoadingCards.vue";
import Panorama from "@/components/Panorama.vue";
import CockpitSlider from "@/components/SliderCockpit.vue";
import Screen from "@/components/Screen.vue";
import Slider from "@/components/Slider.vue";
import HeaderOverlay from "../components/HeaderOverlay";
import {aircraftConfig, LufthansaColors, settings} from "../config";
import Lat, {initLAT, LATEventBus} from "../modules/LAT/LAT_main";
import Vue from "vue";
import {createMarker} from "../modules/LAT/panorama";
import {procedureTeacher} from "../modules/ProcedureTeacher";
import Infospots from "../components/Infospots";
import {setupFlappyBird, startGame} from "../modules/bird/flappybirdgame";
import {httpTour} from "@/api/http";

export default {
    name: "cockpit",
    emits: ['loadedPanorama'],
    data: function () {
        return {
            loadingPic: "",
            loading: true,
            counting: true,
            screenOn: false,
            sliderOn: false,
            teacher: null,
            activeProcedure: null,
            handbookview: true,
        };
    },
    computed: {
        procedures() {
            return aircraftConfig.procedures;
        },
        sliderLeft() {
            return this.genSlider('left');
        },
        sliderRight() {
            return this.genSlider('right');
        },
        aircraftConfig() {
            return aircraftConfig;
        }
    },
    mounted() {
        // use loadingDone method for 3D stuff
        this.loadingPic = aircraftConfig.getAsset('cockpit_loading');

        window.LAT = this.$LAT;
        window.THREE = THREE;
    },
    methods: {

        async startSlider(procedure) {
            if (procedure) {
                const {tour, tourSpots} = await httpTour(procedure.id)
                const tourData = tour.data && JSON.parse(tour.data)[0] ? JSON.parse(tour.data)[0].attributes : null

                procedure.steps = tourSpots.map(tourSpot => ({
                    ...tourSpot,
                    message: tourSpot.title,
                    points: [tourSpot.spot.id],
                    todo: tourSpot.subtitle,
                }))

                procedure.handbook = (tourData && tourData.page) || (tourData && tourData.page_start) || 0;
                procedure.handbookPart = tourData && tourData.handbook_part_path ?  process.env.VUE_APP_API_URL + 'holo-rooms-3d/handbookpart/' + tourData.handbook_part_path : null;
                procedure.handbookkey = tourData ? tourData.handbook : null;
                procedure.page = tourData ? tourData.page : null;
                procedure.page_start = tourData ? tourData.page_start: null
                procedure.page_end = tourData ? tourData.page_end : null

                this.$store.commit('SET_CURRENT_PROCEDURE', procedure)
                this.$log.debug('loaded procedure', tour, tourSpots, procedure);
                this.screenSwitch({value: false, procedure})

                this.teacher = procedureTeacher.selectProcedure(procedure)
                this.activeProcedure = procedure.id;
            }

        },
        sliderVisible(sliderType) {
            let visible = !this.loading && !this.screenOn;

            if (sliderType === 'poi') {
                visible = visible && this.teacher && this.teacher.isRunning() && this.teacher.mode !== 'exam';
            }
            return visible
        },
        genSlider(where) {
            return aircraftConfig.getConfig().slider[where];
        },
        screenSwitch({value, procedure, handbook = true}) {
            value = value === undefined ? !this.screenOn : value;
            this.sliderOn = !!procedure;
            this.screenOn = value;
            this.handbookview = handbook
            if (!procedure && !value && this.teacher) this.teacher.clearView()

            this.$log.debug('screenSwitch', {
                siderOn: this.sliderOn,
                screenOn: this.screenOn,
                handbookview: this.handbookview
            })
        },
        async switchToNextProcedure(procedure) {
            await this.startSlider(procedure)
        },
        toggleHandbook(mode) {
            this.handbookview = mode
        },
        sliderSwitch() {
            this.sliderOn = !this.sliderOn;
        },
        startAppLogic() {
            Vue.$log.info('Start LAT App')
            this.loading = false;
            setTimeout(() => this.$LAT.lookAtObject(new THREE.Vector3(5000, -1500, 0), 0), 100);
        },

        loadingDone: function () {
            this.counting = false;
            this.$emit('loadedPanorama')
            setTimeout(this.startAppLogic.bind(this), settings.DELAY_LOADING_SCREEN_COCKPIT);
        }
    },
    components: {
        Infospots,
        Panorama,
        LoadingCards,
        CockpitSlider,
        Screen,
        Slider,
        HeaderOverlay
    }
};
</script>

<style scoped>
.darker {
    position: absolute;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: #000000;
    opacity: 0.5;
}


/* Animation for Fading Intro Aircraft Picture */
.overlay-leave-active {
    transition: opacity 3s ease-in-out, transform 3s ease-in;
}

.overlay-leave-to {
    opacity: 0;
    transform: scale(2);
}

/* Animation for Screen Background */
.fade-enter-active,
.fade-leave-active {
    transition: 0.5s ease-in-out;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

/* Animation for Screen */
.zoomer-enter-active,
.zoomer-leave-active {
    transition: 0.5s ease-in-out;
}

.zoomer-enter,
.zoomer-leave-to {
    transform: scale(0.8);
    transform: translateY(100%);
}

/* Animation for Sliders */
.slider-left-trans-enter-active,
.slider-left-trans-leave-active {
    transition: 1s ease-out;
}

.slider-left-trans-enter,
.slider-left-trans-leave-to {
    transform: translateX(-200px);
}

.slider-right-trans-enter-active,
.slider-right-trans-leave-active {
    transition: 1s ease-out;
}

.slider-right-trans-enter,
.slider-right-trans-leave-to {
    transform: translateX(200px);
}
</style>
